import { Injectable, inject } from '@angular/core';
import { sanitizeDisplayName, sanitizeEmail } from '../utils/sanitize.utils';
import {
  Auth,
  signInWithEmailAndPassword,
  signInWithCredential,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile as firebaseUpdateProfile,
  reload,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  OAuthProvider,
  onAuthStateChanged,
  signInAnonymously,
  User as FirebaseUser,
  UserCredential
} from '@angular/fire/auth';
import {
  Firestore,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  addDoc,
  serverTimestamp,
  increment,
} from '@angular/fire/firestore';
import { GoogleSignIn } from '@capawesome/capacitor-google-sign-in';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject, ReplaySubject, Observable, from, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { User, SubscriptionTier } from '../models/user.model';
import { CacheService } from './cache.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private cacheService = inject(CacheService);
  
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private authStateSubject = new BehaviorSubject<boolean>(false);
  public authState$ = this.authStateSubject.asObservable();

  /** Emits once (and replays to late subscribers) after Firebase's first onAuthStateChanged fires. */
  private authReadySubject = new ReplaySubject<void>(1);
  public readonly authInitialized$ = this.authReadySubject.asObservable();

  /** True when the current session is a demo (anonymous Firebase auth). */
  private _isDemoSession = false;
  get isDemoSession() { return this._isDemoSession; }

  /** Temp storage for the demo user object while signInAnonymously resolves. */
  private _pendingDemoUser: User | null = null;

  /** Snapshot síncrono del usuario actual (null si no hay sesión). */
  get currentUserValue(): import('../models/user.model').User | null {
    return this.currentUserSubject.value;
  }

  constructor() {
    this.initAuthStateListener();
    this.initializeGoogleAuth();
    this.handleRedirectResult();
  }

  private initAuthStateListener(): void {
    onAuthStateChanged(this.auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Demo: anonymous sign-in — use the pending demo user, skip Firestore lookup
        if (this._isDemoSession && this._pendingDemoUser) {
          const demoUser = { ...this._pendingDemoUser, uid: firebaseUser.uid };
          this._pendingDemoUser = null;
          console.log('[Auth] Demo session started (anonymous uid:', firebaseUser.uid, ')');
          this.currentUserSubject.next(demoUser);
          this.authStateSubject.next(true);
          this.authReadySubject.next();
          return;
        }
        // Anonymous guests (room/friend link spectators) have no Firestore user
        // doc — fetching it just hits a denied (or ad-blocker-blocked) read that
        // can hang and stall auth init. Treat them as authenticated-but-no-profile.
        if (firebaseUser.isAnonymous) {
          console.log('[Auth] onAuthStateChanged - anonymous guest:', firebaseUser.uid);
          this.currentUserSubject.next(null);
          this.authStateSubject.next(true);
          this.authReadySubject.next();
          return;
        }
        console.log('[Auth] onAuthStateChanged - user detected:', firebaseUser.uid, firebaseUser.email);
        const user = await this.getUserData(firebaseUser.uid);
        console.log('[Auth] getUserData result:', user ? 'found' : 'not found in Firestore', user);
        this.currentUserSubject.next(user);
        this.authStateSubject.next(true);
        this.authReadySubject.next();
        this.recordSession(firebaseUser.uid);
      } else {
        console.log('[Auth] onAuthStateChanged - no user (signed out)');
        this.currentUserSubject.next(null);
        this.authStateSubject.next(false);
        this.authReadySubject.next();
      }
    });
  }

  // Called once on init. If the user just returned from a Google redirect sign-in
  // (mobile browser path), resolve their profile the same way as the popup path.
  private async handleRedirectResult(): Promise<void> {
    if (Capacitor.isNativePlatform()) return;
    try {
      const result = await getRedirectResult(this.auth);
      if (!result) return;

      console.log('[Auth] handleRedirectResult - redirect sign-in completed, uid:', result.user.uid);
      const existingUser = await this.getUserData(result.user.uid);
      if (existingUser) {
        this.currentUserSubject.next(existingUser);
        this.authStateSubject.next(true);
        return;
      }

      const newUser: User = {
        uid: result.user.uid,
        email: result.user.email!,
        displayName: result.user.displayName || 'User',
        photoURL: result.user.photoURL || undefined,
        subscriptionTier: SubscriptionTier.FREE,
        friends: [],
        progress: {
          completedVideos: [],
          completedQuizzes: [],
          quizScores: {},
          watchHistory: [],
          currentStreak: 0,
          longestStreak: 0,
          lastActiveDate: new Date(),
          totalXp: 0,
          dailyFirstQuizDone: false,
        },
        stats: { sessionCount: 0, challengesSent: 0, linksCopied: 0, lastSessionAt: new Date() },
        createdAt: new Date(),
      };
      await this.createUserInFirestore(newUser);
      this.currentUserSubject.next(newUser);
      this.authStateSubject.next(true);
    } catch (err) {
      console.error('[Auth] handleRedirectResult error:', err);
    }
  }

  private async initializeGoogleAuth(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      try {
        await GoogleSignIn.initialize({
          clientId: 'YOUR_WEB_CLIENT_ID.apps.googleusercontent.com',
          scopes: ['profile', 'email'],
        });
      } catch (error) {
        console.error('Error initializing Google Auth:', error);
      }
    }
  }

  async signInWithEmail(email: string, password: string): Promise<User> {
    try {
      const credential = await signInWithEmailAndPassword(this.auth, email, password);
      const user = await this.getUserData(credential.user.uid);
      if (!user) {
        throw new Error('Failed to retrieve user data');
      }
      return user;
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  async signUpWithEmail(
    email: string,
    password: string,
    displayName: string
  ): Promise<User> {
    try {
      const cleanEmail       = sanitizeEmail(email);
      const cleanDisplayName = sanitizeDisplayName(displayName);

      const credential = await createUserWithEmailAndPassword(this.auth, cleanEmail, password);

      await firebaseUpdateProfile(credential.user, { displayName: cleanDisplayName });
      await sendEmailVerification(credential.user);

      const newUser: User = {
        uid: credential.user.uid,
        email: credential.user.email!,
        displayName: cleanDisplayName,
        ...(credential.user.photoURL ? { photoURL: credential.user.photoURL } : {}),
        subscriptionTier: SubscriptionTier.FREE,
        friends: [],
        progress: {
          completedVideos: [],
          completedQuizzes: [],
          quizScores: {},
          watchHistory: [],
          currentStreak: 0,
          longestStreak: 0,
          lastActiveDate: new Date(),
          totalXp: 0,
          dailyFirstQuizDone: false,
        },
        stats: { sessionCount: 0, challengesSent: 0, linksCopied: 0, lastSessionAt: new Date() },
        createdAt: new Date(),
      };

      await this.createUserInFirestore(newUser);

      return newUser;
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  async signInWithGoogle(): Promise<User> {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');

      // Mobile browsers (Safari on iPhone, Chrome on Android) and desktop Safari
      // block popups or kill them via ITP. Detect and use redirect —
      // handleRedirectResult() picks up the result when the app reloads after
      // Google redirects back.
      const ua = navigator.userAgent;
      const isMobileBrowser = /iPhone|iPad|iPod|Android/i.test(ua);
      const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
      if (isMobileBrowser || isSafari) {
        console.log('[Auth] signInWithGoogle - mobile/Safari detected, using redirect...');
        await signInWithRedirect(this.auth, provider);
        // Page navigates away — this line never executes.
        return new Promise(() => {});
      }

      console.log('[Auth] signInWithGoogle - starting popup...');
      let credential;
      try {
        credential = await signInWithPopup(this.auth, provider);
      } catch (popupError: any) {
        // Safari (and other browsers) block popups not opened in a direct user
        // gesture, or via ITP cross-origin restrictions. Fall back to redirect.
        const code = popupError?.code;
        if (
          code === 'auth/popup-blocked' ||
          code === 'auth/popup-closed-by-user' ||
          code === 'auth/cancelled-popup-request' ||
          code === 'auth/operation-not-supported-in-this-environment'
        ) {
          console.log('[Auth] signInWithGoogle - popup blocked, falling back to redirect...');
          await signInWithRedirect(this.auth, provider);
          // Page navigates away — never resolves here.
          return new Promise(() => {});
        }
        throw popupError;
      }
      console.log('[Auth] signInWithGoogle - popup success, uid:', credential.user.uid);

      const existingUser = await this.getUserData(credential.user.uid);
      if (existingUser) {
        console.log('[Auth] signInWithGoogle - existing user found in Firestore:', existingUser);
        this.currentUserSubject.next(existingUser);
        this.authStateSubject.next(true);
        return existingUser;
      }

      const newUser: User = {
        uid: credential.user.uid,
        email: credential.user.email!,
        displayName: credential.user.displayName || 'User',
        photoURL: credential.user.photoURL || undefined,
        subscriptionTier: SubscriptionTier.FREE,
        friends: [],
        progress: {
          completedVideos: [],
          completedQuizzes: [],
          quizScores: {},
          watchHistory: [],
          currentStreak: 0,
          longestStreak: 0,
          lastActiveDate: new Date(),
          totalXp: 0,
          dailyFirstQuizDone: false,
        },
        stats: { sessionCount: 0, challengesSent: 0, linksCopied: 0, lastSessionAt: new Date() },
        createdAt: new Date(),
      };

      console.log('[Auth] signInWithGoogle - new user, creating in Firestore:', newUser);
      await this.createUserInFirestore(newUser);
      this.currentUserSubject.next(newUser);
      this.authStateSubject.next(true);
      return newUser;
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  async signInWithGoogleNative(): Promise<User> {
    try {
      if (!Capacitor.isNativePlatform()) {
        return this.signInWithGoogle();
      }

      console.log('[Auth] signInWithGoogleNative - starting native sign-in...');
      const result = await GoogleSignIn.signIn();
      const idToken = (result as any).idToken ?? (result as any).authentication?.idToken;
      const credential = GoogleAuthProvider.credential(idToken ?? null);
      const userCredential = await signInWithCredential(this.auth, credential);
      console.log('[Auth] signInWithGoogleNative - success, uid:', userCredential.user.uid);

      const existingUser = await this.getUserData(userCredential.user.uid);
      if (existingUser) {
        console.log('[Auth] signInWithGoogleNative - existing user found:', existingUser);
        this.currentUserSubject.next(existingUser);
        this.authStateSubject.next(true);
        return existingUser;
      }

      const newUser: User = {
        uid: userCredential.user.uid,
        email: result.email ?? '',
        displayName: result.givenName + ' ' + result.familyName,
        photoURL: result.imageUrl || undefined,
        subscriptionTier: SubscriptionTier.FREE,
        friends: [],
        progress: {
          completedVideos: [],
          completedQuizzes: [],
          quizScores: {},
          watchHistory: [],
          currentStreak: 0,
          longestStreak: 0,
          lastActiveDate: new Date(),
          totalXp: 0,
          dailyFirstQuizDone: false,
        },
        stats: { sessionCount: 0, challengesSent: 0, linksCopied: 0, lastSessionAt: new Date() },
        createdAt: new Date(),
      };

      console.log('[Auth] signInWithGoogleNative - new user, creating in Firestore:', newUser);
      await this.createUserInFirestore(newUser);
      this.currentUserSubject.next(newUser);
      this.authStateSubject.next(true);
      return newUser;
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  async signInWithApple(): Promise<User> {
    try {
      if (!Capacitor.getPlatform() || Capacitor.getPlatform() !== 'ios') {
        throw new Error('Apple Sign-In is only available on iOS');
      }

      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      
      const credential = await signInWithPopup(this.auth, provider);
      
      const existingUser = await this.getUserData(credential.user.uid);
      if (existingUser) {
        return existingUser;
      }
      
      const newUser: User = {
        uid: credential.user.uid,
        email: credential.user.email!,
        displayName: credential.user.displayName || 'User',
        photoURL: credential.user.photoURL || undefined,
        subscriptionTier: SubscriptionTier.FREE,
        friends: [],
        progress: {
          completedVideos: [],
          completedQuizzes: [],
          quizScores: {},
          watchHistory: [],
          currentStreak: 0,
          longestStreak: 0,
          lastActiveDate: new Date(),
          totalXp: 0,
          dailyFirstQuizDone: false,
        },
        stats: { sessionCount: 0, challengesSent: 0, linksCopied: 0, lastSessionAt: new Date() },
        createdAt: new Date(),
      };

      await this.createUserInFirestore(newUser);

      return newUser;
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  async signOut(): Promise<void> {
    try {
      if (Capacitor.isNativePlatform() && !this._isDemoSession) {
        await GoogleSignIn.signOut();
      }
      // Always sign out of Firebase (demo uses anonymous auth, also needs sign out)
      await firebaseSignOut(this.auth);
      await this.cacheService.clearAll();
      this._isDemoSession = false;
      this._pendingDemoUser = null;
      this.currentUserSubject.next(null);
      this.authStateSubject.next(false);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  async resendVerificationEmail(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('No authenticated user');
    try {
      await sendEmailVerification(user);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  async reloadCurrentUser(): Promise<boolean> {
    const user = this.auth.currentUser;
    if (!user) return false;
    await reload(user);
    return user.emailVerified;
  }

  isEmailVerified(): boolean {
    return this.auth.currentUser?.emailVerified ?? false;
  }

  /** True when the active Firebase session is an anonymous (guest) user. */
  isAnonymous(): boolean {
    return this.auth.currentUser?.isAnonymous ?? false;
  }

  getCurrentUser(): Observable<User | null> {
    return this.currentUser$;
  }

  /** Re-fetch the current user's Firestore profile and broadcast it. */
  async refreshCurrentUser(): Promise<User | null> {
    const fbUser = this.auth.currentUser;
    if (!fbUser || fbUser.isAnonymous) return null;
    const user = await this.getUserData(fbUser.uid);
    this.currentUserSubject.next(user);
    return user;
  }

  isAuthenticated(): Observable<boolean> {
    return this.authState$;
  }

  /**
   * Auto-starts an anonymous guest session for unauthenticated users arriving
   * via a shared link. No-op if a session is already active.
   */
  async startGuestSession(): Promise<void> {
    // Wait for persisted session restore before deciding to go guest, else a
    // cold load (shared link in new tab) would start anon auth for a real user.
    await this.auth.authStateReady();
    if (this._isDemoSession || this.auth.currentUser) return;
    const guest: User = {
      uid: 'guest',
      email: '',
      displayName: 'Guest',
      subscriptionTier: 'free' as any,
      friends: [],
      progress: {
        completedVideos: [],
        completedQuizzes: [],
        quizScores: {} as any,
        watchHistory: [],
        currentStreak: 0,
        longestStreak: 0,
        lastActiveDate: new Date(),
        totalXp: 0,
        dailyFirstQuizDone: false,
      },
      stats: { sessionCount: 0, challengesSent: 0, linksCopied: 0, lastSessionAt: new Date() },
      createdAt: new Date(),
    };
    await this.setDemoUser(guest);
  }

  /**
   * Start a demo session using anonymous Firebase auth.
   * This gives the user a real Firebase token so Firestore rules work,
   * while still marking the session as demo to restrict private features.
   */
  async setDemoUser(user: User): Promise<void> {
    this._isDemoSession = true;
    this._pendingDemoUser = user;
    try {
      await signInAnonymously(this.auth);
      // onAuthStateChanged handles the rest via _pendingDemoUser
    } catch (err) {
      console.warn('[Auth] signInAnonymously failed, falling back to local demo:', err);
      this._pendingDemoUser = null;
      this.currentUserSubject.next(user);
      this.authStateSubject.next(true);
    }
  }

  async updateProfile(displayName?: string, photoURL?: string): Promise<void> {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        throw new Error('No user is currently signed in');
      }

      const cleanDisplayName = displayName
        ? sanitizeDisplayName(displayName)
        : currentUser.displayName ?? undefined;

      await firebaseUpdateProfile(currentUser, {
        displayName: cleanDisplayName,
        photoURL: photoURL || currentUser.photoURL,
      });

      const userDoc = doc(this.firestore, `users/${currentUser.uid}`);
      await setDoc(
        userDoc,
        {
          displayName: cleanDisplayName,
          photoURL: photoURL || currentUser.photoURL,
        },
        { merge: true }
      );

      const updatedUser = await this.getUserData(currentUser.uid);
      this.currentUserSubject.next(updatedUser);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  private async getUserData(uid: string): Promise<User | null> {
    try {
      console.log('[Auth] getUserData - fetching Firestore doc for uid:', uid);
      const userDoc = doc(this.firestore, `users/${uid}`);
      const userSnap = await getDoc(userDoc);

      if (userSnap.exists()) {
        const data = userSnap.data();
        console.log('[Auth] getUserData - raw Firestore data:', data);
        const user = {
          ...data,
          createdAt: data['createdAt']?.toDate() || new Date(),
          progress: {
            ...data['progress'],
            lastActiveDate: data['progress']?.lastActiveDate?.toDate() || new Date(),
          },
        } as User;
        console.log('[Auth] getUserData - parsed user:', user);
        return user;
      }

      console.warn('[Auth] getUserData - no Firestore doc found for uid:', uid);
      return null;
    } catch (error) {
      console.error('[Auth] getUserData - error:', error);
      return null;
    }
  }

  /** Registra una entrada a la app: crea doc en sessions/ e incrementa stats. */
  async recordSession(uid: string): Promise<void> {
    if (this._isDemoSession) return;
    try {
      const sessionsCol = collection(this.firestore, `users/${uid}/sessions`);
      await addDoc(sessionsCol, {
        enteredAt: serverTimestamp(),
        platform: this.detectPlatform(),
      });
      const userDoc = doc(this.firestore, `users/${uid}`);
      await updateDoc(userDoc, {
        'stats.sessionCount': increment(1),
        'stats.lastSessionAt': serverTimestamp(),
      });
    } catch {
      // no interrumpir la sesión si falla el tracking
    }
  }

  /** Incrementa un contador de acción de challenge en el doc del usuario. */
  async recordChallengeAction(uid: string, type: 'challengesSent' | 'linksCopied'): Promise<void> {
    if (this._isDemoSession) return;
    try {
      const actionsCol = collection(this.firestore, `users/${uid}/challengeActions`);
      await addDoc(actionsCol, {
        type,
        createdAt: serverTimestamp(),
      });
      const userDoc = doc(this.firestore, `users/${uid}`);
      await updateDoc(userDoc, { [`stats.${type}`]: increment(1) });
    } catch {
      // no interrumpir el flujo si falla el tracking
    }
  }

  private detectPlatform(): string {
    if (Capacitor.isNativePlatform()) return Capacitor.getPlatform();
    return 'web';
  }

  private async createUserInFirestore(user: User): Promise<void> {
    try {
      const userDoc = doc(this.firestore, `users/${user.uid}`);
      const data: Record<string, any> = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        subscriptionTier: user.subscriptionTier,
        friends: user.friends,
        progress: { ...user.progress, lastActiveDate: serverTimestamp() },
        stats: user.stats,
        createdAt: serverTimestamp(),
      };
      if (user.photoURL) data['photoURL'] = user.photoURL;
      await setDoc(userDoc, data);
    } catch (error) {
      console.error('Error creating user in Firestore:', error);
      throw error;
    }
  }

  /** Updates in-memory user progress after quiz completion — no Firestore read. */
  updateCachedQuizProgress(quizId: string, score: number, maxScore: number, pct: number): void {
    const current = this.currentUserSubject.value;
    if (!current) return;
    const updated: User = {
      ...current,
      progress: {
        ...current.progress,
        completedQuizzes: current.progress.completedQuizzes?.includes(quizId)
          ? current.progress.completedQuizzes
          : [...(current.progress.completedQuizzes ?? []), quizId],
        quizScores: {
          ...current.progress.quizScores,
          [quizId]: { score, maxScore, pct, completedAt: new Date() },
        },
      },
    };
    this.currentUserSubject.next(updated);
  }

  private handleAuthError(error: any): Error {
    let errorMessage = 'An error occurred during authentication';
    
    switch (error.code) {
      case 'auth/user-not-found':
        errorMessage = 'No user found with this email address';
        break;
      case 'auth/wrong-password':
        errorMessage = 'Incorrect password';
        break;
      case 'auth/email-already-in-use':
        errorMessage = 'An account already exists with this email';
        break;
      case 'auth/weak-password':
        errorMessage = 'Password should be at least 6 characters';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Invalid email address';
        break;
      case 'auth/user-disabled':
        errorMessage = 'This account has been disabled';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Too many attempts. Please try again later';
        break;
      case 'auth/network-request-failed':
        errorMessage = 'Network error. Please check your connection';
        break;
      case 'auth/popup-closed-by-user':
        errorMessage = 'Sign-in popup was closed';
        break;
      default:
        errorMessage = error.message || errorMessage;
    }
    
    return new Error(errorMessage);
  }
}
